import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, hasFirebaseConfig } from '../lib/firebase';
import type { AchievementUnlockRecord } from '../lib/achievements';

function toNumber(value: unknown, fallback = 0) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function toString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function toAchievementUnlockRecord(data: Record<string, unknown>, fallbackUnlockKey: string): AchievementUnlockRecord {
  return {
    unlockKey: toString(data.unlockKey, fallbackUnlockKey),
    definitionId: toString(data.definitionId),
    tier: toNumber(data.tier, 0),
    scopeKey: toString(data.scopeKey, 'all') as AchievementUnlockRecord['scopeKey'],
    category: toString(data.category, 'starter') as AchievementUnlockRecord['category'],
    celebration: toString(data.celebration, 'toast') as AchievementUnlockRecord['celebration'],
    priority: toNumber(data.priority, 0),
    icon: toString(data.icon, 'spark') as AchievementUnlockRecord['icon'],
    threshold: toNumber(data.threshold, 0),
    value: toNumber(data.value, 0),
    unlockedAt: toString(data.unlockedAt, new Date(0).toISOString()),
  };
}

export async function fetchAchievementUnlocksFromCloud(userId: string): Promise<AchievementUnlockRecord[]> {
  if (!hasFirebaseConfig || !db || !userId) {
    return [];
  }

  const firestore = db;
  const snapshot = await getDocs(collection(firestore, 'users', userId, 'achievementUnlocks'));

  return snapshot.docs.map((unlockDoc) => {
    const data = unlockDoc.data() as Record<string, unknown>;
    return toAchievementUnlockRecord(data, unlockDoc.id);
  });
}

export async function syncAchievementUnlocksToCloud(
  userId: string,
  unlocks: AchievementUnlockRecord[],
): Promise<void> {
  if (!hasFirebaseConfig || !db || !userId || unlocks.length === 0) {
    return;
  }

  const firestore = db;

  await Promise.all(
    unlocks.map((unlock) =>
      setDoc(
        doc(firestore, 'users', userId, 'achievementUnlocks', unlock.unlockKey),
        {
          ...unlock,
          userId,
          syncedAt: serverTimestamp(),
        },
        { merge: true },
      ),
    ),
  );
}
